import React, { ReactElement, useEffect, useState } from "react";
import classNames from "classnames";
import {
  Button,
  Col,
  Drawer,
  Input,
  InputNumber,
  LegacyDatePicker,
  LegacyForm,
  Radio,
  Row,
  Select,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { CouponUsed } from "@payping/api";
import type { CouponCreateV2ViewModel, CouponEditV2ViewModel, ProductListItemWithImageViewModel } from "@payping/api";
import { createCoupon, updateCoupon } from "@payping/redux/actions";
import { dateFormats, isBeforeToday, jalaliNow, toUtc } from "@payping/utils/dateUtils";
import { DashboardRoutes } from "@/router/constant";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppDispatch } from "@/utils/redux";
import { useAppSelector } from "@/utils";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { InfoCircleFilled } from "@payping/icons";
import HandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import DrawerCloseButton from "@/components/Buttons/DrawerCloseButton";
import styles from "@/assets/scss/Shop/Product/form.module.scss";
import ProductSelect from "./ProductSelect";
import { CouponFormType } from "./types";
import { CouponType, FormWrapperProps } from "../types";
import { numberFormatter } from "@payping/utilities-shared/number";

const CustomCouponForm: React.FC<FormWrapperProps> = (props: FormWrapperProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const { title, loadingCoupon, locationSearch } = props;
  const { code } = useParams<{ code: string }>() as { code: string };
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const [couponFormRef] = LegacyForm.useForm<CouponFormType>();
  const navigate = useNavigate();
  const couponDetails = useAppSelector((state) => state.coupons.details);
  const dateFormat = dateFormats.ui.utcDateTime;
  const backFormat = dateFormats.standard.utcDateTime;
  const [productList, setProductList] = useState<ProductListItemWithImageViewModel[]>([]);

  function goToList() {
    navigate({
      pathname: DashboardRoutes.myShop.coupon.list.url,
      search: locationSearch,
    });
  }

  useEffect(() => {
    if (couponDetails) {
      couponFormRef.setFieldsValue({
        ...couponDetails,
        activeProductCode: couponDetails.activeProductCode?.split(","),
        allProducts: !couponDetails.activeProductCode,
        amount: String(couponDetails.amount),
        maxRedemption: String(couponDetails.maxRedemption),
        redeemDateTime: toUtc({
          convertTime: false,
          date: couponDetails.redeemDateTime,
          dateFormat: backFormat,
          toFormat: backFormat,
        }).object,
      });
    }
  }, [backFormat, couponDetails, couponFormRef]);

  useEffect(() => {
    if (!code) couponFormRef.resetFields();
  }, [couponFormRef, code]);

  const [{ loading: loadingCreate }, handleCreate] = useMessagedAsyncFn(
    async (couponData: CouponCreateV2ViewModel): Promise<any> => {
      await dispatch(createCoupon({ ...couponData }));
    },
    [dispatch],
    undefined,
    { success: false, useProgress: true },
  );

  const [{ loading: loadingEdit }, handleEdit] = useMessagedAsyncFn(
    async (couponData: CouponEditV2ViewModel) => {
      if (code) {
        await dispatch(updateCoupon(couponData.code || code, { ...couponData }));
      }
    },
    [code, dispatch],
    undefined,
    { success: false, useProgress: true },
  );

  const loading = loadingCoupon || (code ? loadingEdit : loadingCreate);

  const validateAmount = (value: string, type: CouponType): Promise<void> => {
    const val = Number(value);
    switch (type) {
      case CouponType.Toman:
        return value && val < 1000 ? Promise.reject(t("error.minimum", { quantity: 1000 })) : Promise.resolve();
      case CouponType.Percent:
      default:
        return value && !(val > 0 && val < 100)
          ? Promise.reject(t("error.between", { max: 100, min: 0 }))
          : Promise.resolve();
    }
  };

  return (
    <Drawer
      open={isDrawerOpen}
      title={title}
      height={"100%"}
      closeIcon={false}
      placement={"bottom"}
      extra={<DrawerCloseButton setIsDrawerOpen={setIsDrawerOpen} />}
    >
      <HandlingCrispInMobile>
        <LegacyForm
          canDeactivate
          className={classNames(styles.form, styles.lastForm)}
          initialValues={{
            redeemDateTime: jalaliNow({ manipulate: [{ args: [1, "week"], method: "add" }] }).object,
            type: CouponType.Toman,
            couponUsed: CouponUsed.Item,
          }}
          onFinish={async ({ allProducts, ...values }: CouponFormType): Promise<void> => {
            const data = {
              ...values,
              activeProductCode: allProducts ? undefined : values.activeProductCode,
              amount: Number(values.amount),
              maxRedemption: Number(values.maxRedemption),
              redeemDateTime: toUtc({
                convertTime: false,
                date: values.redeemDateTime,
                dateFormat,
                toFormat: backFormat,
              }).string,
            };
            const error = code ? await handleEdit({ code, ...data }) : await handleCreate(data);
            if (!error) goToList();
          }}
          form={couponFormRef}
          name={"coupon"}
          layout={"vertical"}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} lg={12}>
              <LegacyForm.Item label={t("coupon.table.name")} name={"name"} rules={[{ required: true }]}>
                <Input disabled={loading} />
              </LegacyForm.Item>
            </Col>
            <Col xs={24} lg={12}>
              <LegacyForm.Item label={t("expire")} name={"redeemDateTime"} rules={[{ required: true }]}>
                <LegacyDatePicker
                  skeleton={loading}
                  className={classNames(styles.flex, "tw:w-full")}
                  disabledDate={(date) => isBeforeToday(date)}
                  showTime
                  futureYears={50}
                />
              </LegacyForm.Item>
            </Col>
            <Col xs={24} lg={9}>
              <LegacyForm.Item
                label={t("coupon.table.code")}
                name={"userCouponCode"}
                rules={[
                  { required: true },
                  {
                    validator: (_, value): Promise<void> =>
                      value && !(value.length >= 5)
                        ? Promise.reject(t("error.minimumLength", { min: 5 }))
                        : Promise.resolve(),
                  },
                ]}
              >
                <Input disabled={loading} />
              </LegacyForm.Item>
            </Col>
            <Col xs={24} lg={9}>
              <LegacyForm.Item
                required
                label={t("coupon.table.amount")}
                shouldUpdate={(prev, curr): boolean => prev.type !== curr.type}
              >
                {(form1): ReactElement => {
                  const type = form1.getFieldValue("type");
                  return (
                    <LegacyForm.Item
                      name={"amount"}
                      className={"tw:w-full"}
                      rules={[
                        { required: true },
                        {
                          validator: (_, value) => validateAmount(value, type),
                        },
                      ]}
                    >
                      <InputNumber
                        formatter={(value) => numberFormatter.format(value)}
                        parser={(value) => numberFormatter.parse(value)}
                        disabled={loading}
                        className="tw:w-full!"
                        inputMode="numeric"
                        changeOnWheel={false}
                        controls={false}
                        addonAfter={
                          <LegacyForm.Item noStyle name={"type"}>
                            <Select disabled={loading}>
                              <Select.Option value={CouponType.Percent}>{t("coupon.price.percent")}</Select.Option>
                              <Select.Option value={CouponType.Toman}>{t("coupon.price.toman")}</Select.Option>
                            </Select>
                          </LegacyForm.Item>
                        }
                      />
                    </LegacyForm.Item>
                  );
                }}
              </LegacyForm.Item>
            </Col>
            <Col xs={24} lg={6}>
              <LegacyForm.Item
                label={t("coupon.detail.maxRedemption")}
                name={"maxRedemption"}
                rules={[
                  { required: true },
                  {
                    validator: (_, value): Promise<void> =>
                      value && !(Number(value) >= 1)
                        ? Promise.reject(t("error.minimum", { quantity: 1 }))
                        : Promise.resolve(),
                  },
                ]}
              >
                <InputNumber
                  formatter={(value) => numberFormatter.format(value, { maxDecimals: 2 })}
                  parser={(value) => numberFormatter.parse(value)}
                  disabled={loading}
                  className="tw:w-full!"
                  inputMode="numeric"
                  changeOnWheel={false}
                  controls={false}
                />
              </LegacyForm.Item>
            </Col>
          </Row>
          <Space className={"tw:w-full"} orientation={"vertical"}>
            <Space className={"tw:w-full"} orientation={"vertical"}>
              <Typography.Text>{t("coupon.type")}</Typography.Text>
              <LegacyForm.Item name={"couponUsed"}>
                <Radio.Group>
                  <Radio.Button value={CouponUsed.Item}>{t("shop.tabs.product")}</Radio.Button>
                  <Radio.Button value={CouponUsed.Invoice}>{t("tabs.invoice")}</Radio.Button>
                </Radio.Group>
              </LegacyForm.Item>
            </Space>

            <Row className={"tw:my-4! tw:w-full"}>
              <Col xs={24} lg={24}>
                <LegacyForm.Item dependencies={["couponUsed"]}>
                  {(form1): ReactElement => {
                    const invoiceOrProduct = form1.getFieldValue("couponUsed");
                    if (invoiceOrProduct === CouponUsed.Item)
                      return (
                        <ProductSelect productList={productList} setProductList={setProductList} loading={loading} />
                      );
                    return (
                      <Space className={"color--cta"}>
                        <InfoCircleFilled width={14} />
                        <Typography.Text>{t("invoice.activeForAll")}</Typography.Text>
                      </Space>
                    );
                  }}
                </LegacyForm.Item>
              </Col>
            </Row>
            <Space className={"tw:w-full"}>
              <Button type={"primary"} htmlType={"submit"} loading={loading}>
                {t("save")}
              </Button>
              <Link to={{ pathname: DashboardRoutes.myShop.coupon.list.url, search: locationSearch }}>
                <Button variant={"outlined"} color="primary">
                  {t("return")}
                </Button>
              </Link>
            </Space>
          </Space>
        </LegacyForm>
      </HandlingCrispInMobile>
    </Drawer>
  );
};

export default CustomCouponForm;
